import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Droplets,
  Bell,
  Zap,
  FileText,
  ArrowRight,
  ArrowDown,
  CheckCircle,
  Shield,
  Smartphone,
  LayoutDashboard,
  Wrench,
  Users,
  BarChart3,
  Building2,
  Lock,
  History,
  Database,
  Settings,
  Eye,
  ChevronDown,
  ChevronUp,
  TrendingDown,
  Timer,
  Heart,
  LineChart,
  Repeat,
  Layers,
  GitBranch,
  Palette,
  Calculator,
  AlertTriangle,
  Phone,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { TopBar } from '@/components/TopBar';
import { Footer } from '@/components/Footer';
import { HreflangTags } from '@/components/HreflangTags';
import { ContactForm } from '@/components/sections/ContactForm';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { e2en, e2lv } from '@/i18n/e2Content_en_lv';
import { e2lt, e2ee } from '@/i18n/e2Content_lt_ee';

const CANONICAL = 'https://0gbaltics.com/e2-insurance';
const CONTENT = { en: e2en, lv: e2lv, lt: e2lt, ee: e2ee };

const VALUE_ICONS = [Droplets, Bell, Zap, FileText];
const METRIC_ICONS = [Droplets, Bell, CheckCircle];
const METRIC_ABBRS = ['TTD', 'TTA', 'TTR'];
const GAIN_ICONS = [TrendingDown, Timer, Heart, BarChart3, Repeat, Layers];
const INCIDENT_ICONS = [Droplets, Bell, CheckCircle, Phone, Wrench, CheckCircle];
const KPI_ICONS = [Building2, AlertTriangle, Timer, CheckCircle];
const KPI_ACCENTS = ['text-white', 'text-amber-400', 'text-teal-300', 'text-emerald-400'];
const STATUS_COLORS = [
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-red-100 text-red-700',
  'bg-blue-100 text-blue-700',
  'bg-teal-100 text-teal-700',
];
const PLATFORM_ICONS = [Smartphone, LayoutDashboard, Wrench];
const FITS_ICONS = [Users, Phone, GitBranch, Building2];
const TRUST_ICONS = [Lock, History, Shield, Database, Eye];

const E2InsurancePage = () => {
  const { language } = useLanguage();
  const c = CONTENT[language] || CONTENT.en;
  const [openFaq, setOpenFaq] = useState(null);
  const [calc, setCalc] = useState({
    properties: 10000,
    frequency: 2,
    avgCost: 3000,
    reduction: 10,
  });

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const handleCalcChange = (field, value) => {
    const num = value === '' ? '' : Math.max(0, Number(value));
    setCalc((prev) => ({ ...prev, [field]: num }));
  };

  const annualClaims = (Number(calc.properties) || 0) * ((Number(calc.frequency) || 0) / 100);
  const exposure = annualClaims * (Number(calc.avgCost) || 0);
  const avoided = exposure * ((Number(calc.reduction) || 0) / 100);
  const fmt = (n) => '€' + Math.round(n).toLocaleString('en-US');

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: c.faq.items.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'E2 Insurance – Water Leak Detection for Insurance',
    serviceType: 'Connected water leak detection and risk prevention platform for insurers',
    provider: { '@type': 'Organization', name: '0g Baltics', url: 'https://0gbaltics.com' },
    description: c.meta.description,
    areaServed: ['Lithuania', 'Latvia', 'Estonia', 'Europe'],
    url: CANONICAL,
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://0gbaltics.com/' },
      { '@type': 'ListItem', position: 2, name: 'E2 Insurance', item: CANONICAL },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{c.meta.title}</title>
        <meta name="description" content={c.meta.description} />
        <meta
          name="keywords"
          content="water leak detection for insurance, insurance water leak prevention, water damage prevention for insurers, connected water leak detection, insurance risk prevention, home insurance leak detection, water damage risk management, smart water leak prevention, property risk prevention platform, insurance claims prevention, connected risk prevention, insurance loss prevention"
        />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={c.meta.title} />
        <meta property="og:description" content={c.meta.description} />
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:url" content={CANONICAL} />
        <meta property="twitter:title" content={c.meta.title} />
        <meta property="twitter:description" content={c.meta.twitterDescription} />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>
      <HreflangTags />

      <TopBar />
      <Header alwaysVisible />

      <main className="pt-20" data-testid="e2-insurance-page">
        {/* ============ 1 — HERO ============ */}
        <section className="bg-slate-900 text-white">
          <div className="container-0g py-20 md:py-28 lg:py-32">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-400 mb-6">
                  <Droplets className="h-4 w-4" />
                  {c.hero.badge}
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] mb-6">
                  {c.hero.h1}
                </h1>
                <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-4 max-w-2xl">
                  {c.hero.sub}
                </p>
                <p className="text-base md:text-lg font-medium text-teal-300 mb-8">
                  {c.hero.supporting}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <Button
                    size="lg"
                    className="bg-teal-600 hover:bg-teal-500 text-white px-8"
                    onClick={() => scrollTo('e2-problem')}
                    data-testid="hero-discover-btn"
                  >
                    {c.hero.ctaPrimary}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-slate-500 text-white bg-transparent hover:bg-slate-800 hover:text-white px-8"
                    onClick={() => scrollTo('contact-form')}
                    data-testid="hero-talk-btn"
                  >
                    {c.hero.ctaSecondary}
                  </Button>
                </div>
                <p className="text-sm text-slate-400">{c.hero.trustLine}</p>
              </div>

              {/* Hero incident card mockup (HTML/CSS) */}
              <div className="lg:col-span-5 hidden md:block" aria-hidden="true">
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 shadow-2xl max-w-md ml-auto">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-teal-400" />
                      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                        {c.hero.liveLabel}
                      </span>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-teal-500/20 text-teal-300 border border-teal-500/30">
                      {c.hero.resolved}
                    </span>
                  </div>
                  <div className="flex items-start gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-teal-500/15 flex items-center justify-center flex-shrink-0">
                      <Droplets className="h-5 w-5 text-teal-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">{c.hero.incidentTitle}</p>
                      <p className="text-sm text-slate-400">{c.hero.incidentLoc}</p>
                    </div>
                  </div>
                  <div className="space-y-0 mb-6">
                    {c.hero.steps.map(([time, label], i, arr) => (
                      <div key={time} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <span className={`w-2 h-2 rounded-full mt-1.5 ${i === arr.length - 1 ? 'bg-emerald-400' : 'bg-teal-500'}`} />
                          {i < arr.length - 1 && <span className="w-px flex-1 bg-slate-700" />}
                        </div>
                        <div className="pb-4">
                          <p className="text-xs font-mono text-slate-500">{time}</p>
                          <p className="text-sm text-slate-300">{label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{c.hero.tta}</p>
                      <p className="text-lg font-semibold text-teal-300">{c.hero.ttaVal}</p>
                    </div>
                    <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{c.hero.ttr}</p>
                      <p className="text-lg font-semibold text-teal-300">{c.hero.ttrVal}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ 2 — THE PROBLEM ============ */}
        <section id="e2-problem" className="py-20 md:py-28 bg-white">
          <div className="container-0g">
            <div className="max-w-3xl">
              <p className="section-label">{c.problem.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-6">
                {c.problem.h2}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-4">{c.problem.p1}</p>
              <p className="text-lg text-slate-600 leading-relaxed mb-10">{c.problem.p2}</p>
              <div className="border-l-4 border-teal-600 bg-teal-50 rounded-r-lg px-6 py-5 mb-8">
                <p className="text-xl md:text-2xl font-semibold text-slate-900">{c.problem.highlight}</p>
              </div>
              <p className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
                {c.problem.statement}
              </p>
            </div>
          </div>
        </section>

        {/* ============ 3 — BEFORE / WITH E2 ============ */}
        <section className="py-20 md:py-28 bg-slate-50">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="section-label">{c.compare.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
                {c.compare.h2}
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Without */}
              <div className="bg-white border border-slate-200 rounded-xl p-8" data-testid="timeline-without">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 uppercase tracking-wide">
                    {c.compare.withoutTitle}
                  </h3>
                </div>
                <ol className="space-y-0">
                  {c.compare.withoutSteps.map((step, i, arr) => (
                    <li key={step} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${i < 3 ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>
                          {i + 1}
                        </span>
                        {i < arr.length - 1 && <span className="w-px h-8 bg-slate-200" />}
                      </div>
                      <p className="text-slate-700 font-medium pt-1.5">{step}</p>
                    </li>
                  ))}
                </ol>
                <div className="mt-8 pt-6 border-t border-slate-100">
                  <p className="text-sm text-slate-500">{c.compare.withoutNote}</p>
                </div>
              </div>

              {/* With E2 */}
              <div className="bg-slate-900 rounded-xl p-8 ring-1 ring-teal-500/30" data-testid="timeline-with-e2">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-lg bg-teal-500/15 flex items-center justify-center">
                    <Droplets className="h-5 w-5 text-teal-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white uppercase tracking-wide">
                    {c.compare.withTitle}
                  </h3>
                </div>
                <ol className="space-y-0">
                  {c.compare.withSteps.map((step, i, arr) => (
                    <li key={step} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${i === arr.length - 1 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-teal-500/20 text-teal-300'}`}>
                          {i === arr.length - 1 ? <CheckCircle className="h-4 w-4" /> : <span className="text-sm font-semibold">{i + 1}</span>}
                        </span>
                        {i < arr.length - 1 && <span className="w-px h-8 bg-slate-700" />}
                      </div>
                      <p className="text-slate-200 font-medium pt-1.5">{step}</p>
                    </li>
                  ))}
                </ol>
                <div className="mt-8 pt-6 border-t border-slate-700">
                  <p className="text-sm text-teal-300">{c.compare.withNote}</p>
                </div>
              </div>
            </div>
            <div className="mt-12 text-center">
              <p className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
                {c.compare.conclusion}
              </p>
            </div>
          </div>
        </section>

        {/* ============ 4 — VALUE PROPOSITION ============ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="section-label">{c.value.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
                {c.value.h2}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {c.value.steps.map((step, i) => {
                const Icon = VALUE_ICONS[i];
                return (
                  <div key={step.title} className="relative bg-slate-50 border border-slate-200 rounded-xl p-7 hover:border-teal-500 transition-colors" data-testid={`value-step-${i}`}>
                    <div className="w-12 h-12 rounded-lg bg-teal-600 flex items-center justify-center mb-5">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <p className="text-xs font-bold text-teal-600 uppercase tracking-widest mb-2">{c.value.stepLabel} {i + 1}</p>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{step.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
                    {i < 3 && (
                      <ArrowRight className="hidden lg:block absolute top-1/2 -right-6 -translate-y-1/2 h-5 w-5 text-teal-400 z-10" aria-hidden="true" />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-10 flex items-center justify-center gap-3 text-slate-500 text-sm font-medium flex-wrap">
              {c.value.steps.map((step, i) => (
                <span key={step.title} className="flex items-center gap-3">
                  <span className="text-slate-900 font-semibold">{step.title}</span>
                  {i < 3 && <ArrowRight className="h-4 w-4 text-teal-500" />}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 5 — THREE METRICS ============ */}
        <section className="py-20 md:py-28 bg-slate-900 text-white">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">{c.metrics.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">{c.metrics.h2}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {c.metrics.items.map((m, i) => {
                const Icon = METRIC_ICONS[i];
                return (
                  <div key={m.label} className="bg-slate-800 border border-slate-700 rounded-xl p-8 hover:border-teal-500/50 transition-colors" data-testid={`metric-${METRIC_ABBRS[i].toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-6">
                      <Icon className="h-6 w-6 text-teal-400" />
                      <span className="text-4xl md:text-5xl font-bold text-teal-500/25 tracking-tight">{METRIC_ABBRS[i]}</span>
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{m.label}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                );
              })}
            </div>
            <p className="mt-12 text-lg md:text-xl text-teal-300 font-medium text-center">
              {c.metrics.statement}
            </p>
          </div>
        </section>

        {/* ============ 6 — WHAT INSURERS GAIN ============ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="section-label">{c.gains.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
                {c.gains.h2}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {c.gains.items.map((b, i) => {
                const Icon = GAIN_ICONS[i];
                const primary = i < 3;
                return (
                  <div
                    key={b.title}
                    className={`rounded-xl p-8 transition-colors ${
                      primary
                        ? 'bg-slate-900 text-white ring-1 ring-teal-500/30 hover:ring-teal-400/60'
                        : 'bg-slate-50 border border-slate-200 hover:border-teal-500'
                    }`}
                    data-testid={`benefit-card-${i}`}
                  >
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-5 ${primary ? 'bg-teal-500/15' : 'bg-teal-600/10'}`}>
                      <Icon className={`h-6 w-6 ${primary ? 'text-teal-400' : 'text-teal-600'}`} />
                    </div>
                    <h3 className={`text-lg font-semibold mb-2 ${primary ? 'text-white' : 'text-slate-900'}`}>{b.title}</h3>
                    <p className={`text-sm leading-relaxed ${primary ? 'text-slate-300' : 'text-slate-600'}`}>{b.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============ 7 — INCIDENT TIMELINE ============ */}
        <section className="py-20 md:py-28 bg-slate-50">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="section-label">{c.incident.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4">
                {c.incident.h2}
              </h2>
              <p className="text-lg text-slate-600">{c.incident.sub}</p>
            </div>
            <div className="max-w-2xl mx-auto">
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="incident-timeline-card">
                <div className="px-6 md:px-8 py-5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                      <Droplets className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{c.incident.cardTitle}</p>
                      <p className="text-xs text-slate-500">{c.incident.cardId}</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200">
                    <CheckCircle className="h-3.5 w-3.5" />
                    {c.incident.status}
                  </span>
                </div>
                <div className="px-6 md:px-8 py-6">
                  {c.incident.events.map(([time, label], i, arr) => {
                    const Icon = INCIDENT_ICONS[i];
                    return (
                      <div key={time} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${i === arr.length - 1 ? 'bg-emerald-100' : 'bg-teal-50'}`}>
                            <Icon className={`h-4 w-4 ${i === arr.length - 1 ? 'text-emerald-600' : 'text-teal-600'}`} />
                          </div>
                          {i < arr.length - 1 && <span className="w-px h-6 bg-slate-200" />}
                        </div>
                        <div className="flex items-baseline gap-3 pt-1.5 min-w-0">
                          <span className="text-xs font-mono text-slate-400 flex-shrink-0">{time}</span>
                          <p className="text-sm md:text-base text-slate-700 font-medium">{label}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="px-6 md:px-8 py-5 bg-slate-50 border-t border-slate-100 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{c.incident.tta}</p>
                    <p className="text-xl font-semibold text-slate-900">{c.incident.ttaVal}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{c.incident.ttr}</p>
                    <p className="text-xl font-semibold text-slate-900">{c.incident.ttrVal}</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-4 text-center">{c.incident.note}</p>
            </div>
          </div>
        </section>

        {/* ============ 8 & 9 — CUSTOMER EXPERIENCE + E2 HOME ============ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-0g">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="section-label">{c.customer.label}</p>
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-6">
                  {c.customer.h2}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-4">{c.customer.p1}</p>
                <p className="text-lg text-slate-600 leading-relaxed mb-10">{c.customer.p2}</p>

                <h3 className="text-xl font-semibold text-slate-900 mb-1 flex items-center gap-2">
                  <Smartphone className="h-5 w-5 text-teal-600" />
                  E2 Home
                </h3>
                <p className="text-slate-600 mb-6">{c.customer.e2homeSub}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-8">
                  {c.customer.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="border-l-4 border-teal-600 bg-teal-50 rounded-r-lg px-5 py-4">
                  <p className="font-medium text-slate-900">{c.customer.highlight}</p>
                </div>
              </div>

              {/* Mobile UI mockup — pure HTML/CSS */}
              <div className="flex justify-center" data-testid="e2-home-mockup">
                <div className="w-[300px] sm:w-[320px] bg-slate-900 rounded-[2.5rem] p-3 shadow-2xl" aria-label="E2 Home mobile app interface example" role="img">
                  <div className="bg-slate-50 rounded-[2rem] overflow-hidden">
                    <div className="flex items-center justify-between px-6 pt-4 pb-2">
                      <span className="text-[11px] font-semibold text-slate-900">14:32</span>
                      <span className="flex gap-1">
                        <span className="w-3.5 h-1.5 rounded-full bg-slate-300" />
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                      </span>
                    </div>
                    <div className="px-6 py-3 flex items-center justify-between">
                      <p className="text-lg font-bold text-slate-900">E2 Home</p>
                      <span className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                        <Shield className="h-4 w-4 text-teal-600" />
                      </span>
                    </div>
                    <div className="mx-4 rounded-xl bg-white border border-slate-200 px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-[11px] text-slate-500">{c.customer.mockup.homeStatus}</p>
                        <p className="text-sm font-semibold text-emerald-600 flex items-center gap-1">
                          <CheckCircle className="h-3.5 w-3.5" /> {c.customer.mockup.protected}
                        </p>
                      </div>
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    </div>
                    <div className="mx-4 mt-3 rounded-xl bg-white border-2 border-teal-500 px-4 py-4 shadow-md">
                      <div className="flex items-start gap-3 mb-3">
                        <span className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                          <Droplets className="h-5 w-5 text-teal-600" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-slate-900">{c.customer.mockup.alertTitle}</p>
                          <p className="text-xs text-slate-500">{c.customer.mockup.alertLoc}</p>
                        </div>
                      </div>
                      <div className="rounded-lg bg-slate-50 px-3 py-2 mb-3">
                        <p className="text-[11px] text-slate-500">{c.customer.mockup.recommended}</p>
                        <p className="text-xs font-medium text-slate-800">{c.customer.mockup.recommendedAction}</p>
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        <span className="block text-center text-xs font-semibold text-white bg-teal-600 rounded-lg py-2.5">
                          {c.customer.mockup.btnCheck}
                        </span>
                        <span className="block text-center text-xs font-semibold text-slate-700 bg-slate-100 rounded-lg py-2.5">
                          {c.customer.mockup.btnAssist}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 px-8 py-4 flex items-center justify-between border-t border-slate-200 bg-white">
                      <Shield className="h-5 w-5 text-teal-600" />
                      <Bell className="h-5 w-5 text-slate-300" />
                      <History className="h-5 w-5 text-slate-300" />
                      <Settings className="h-5 w-5 text-slate-300" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ 10 — CONTROL CENTER ============ */}
        <section className="py-20 md:py-28 bg-slate-900">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">{c.control.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4">
                {c.control.h2}
              </h2>
              <p className="text-lg text-slate-400">{c.control.sub}</p>
            </div>

            <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden shadow-2xl" data-testid="control-center-mockup" role="img" aria-label="E2 Insurance Control Center dashboard interface example">
              <div className="flex items-center justify-between px-5 md:px-7 py-4 border-b border-slate-800 bg-slate-900/60">
                <div className="flex items-center gap-3">
                  <LayoutDashboard className="h-5 w-5 text-teal-400" />
                  <p className="text-sm md:text-base font-semibold text-white">E2 Insurance Control Center</p>
                </div>
                <span className="hidden sm:inline-flex text-[10px] font-semibold px-2.5 py-1 rounded-full bg-slate-800 text-slate-400 border border-slate-700">
                  {c.control.sampleTag}
                </span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 p-5 md:p-7">
                {c.control.kpis.map((kpi, i) => {
                  const Icon = KPI_ICONS[i];
                  return (
                    <div key={kpi.label} className="bg-slate-900 border border-slate-800 rounded-lg p-4 md:p-5">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[10px] md:text-[11px] uppercase tracking-wider text-slate-500 pr-2">{kpi.label}</p>
                        <Icon className="h-4 w-4 text-slate-600 flex-shrink-0" />
                      </div>
                      <p className={`text-2xl md:text-3xl font-bold tracking-tight ${KPI_ACCENTS[i]}`}>{kpi.value}</p>
                    </div>
                  );
                })}
              </div>
              {/* incident table (desktop) */}
              <div className="hidden md:block px-7 pb-7">
                <div className="border border-slate-800 rounded-lg overflow-hidden">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-slate-900/80 text-[11px] uppercase tracking-wider text-slate-500">
                        {c.control.tableHeaders.map((h) => (
                          <th key={h} className="px-4 py-3 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {c.control.incidents.map((inc, i) => (
                        <tr key={inc.property} className="border-t border-slate-800/70 text-slate-300">
                          <td className="px-4 py-3.5 font-medium text-white">{inc.property}</td>
                          <td className="px-4 py-3.5">{inc.type}</td>
                          <td className="px-4 py-3.5">{inc.location}</td>
                          <td className="px-4 py-3.5 font-mono text-xs">{inc.detected}</td>
                          <td className="px-4 py-3.5 font-mono text-xs">{inc.ack}</td>
                          <td className="px-4 py-3.5">{inc.assistance}</td>
                          <td className="px-4 py-3.5">
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${STATUS_COLORS[i]}`}>
                              {inc.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* incident feed (mobile) */}
              <div className="md:hidden px-5 pb-5 space-y-3">
                {c.control.incidents.map((inc, i) => (
                  <div key={inc.property} className="bg-slate-900 border border-slate-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-white">{inc.property}</p>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[i]}`}>
                        {inc.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      {inc.type} · {inc.location} · {c.control.detectedShort} {inc.detected}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-4">{c.control.note}</p>
          </div>
        </section>

        {/* ============ 11 — PORTFOLIO INTELLIGENCE ============ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="section-label">{c.portfolio.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4">
                {c.portfolio.h2}
              </h2>
              <p className="text-lg text-slate-600">{c.portfolio.sub}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {c.portfolio.indicators.map((ind) => (
                <div key={ind} className="bg-slate-50 border border-slate-200 rounded-lg p-5 hover:border-teal-500 transition-colors">
                  <LineChart className="h-5 w-5 text-teal-600 mb-3" />
                  <p className="text-sm font-medium text-slate-800">{ind}</p>
                </div>
              ))}
            </div>
            <div className="mt-12 border-l-4 border-teal-600 bg-teal-50 rounded-r-lg px-6 py-5 max-w-3xl">
              <p className="text-lg font-medium text-slate-900">{c.portfolio.highlight}</p>
            </div>
          </div>
        </section>

        {/* ============ 12 — E2 ASSIST ============ */}
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="container-0g">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="section-label">{c.assist.label}</p>
                <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-4">
                  {c.assist.h2}
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  <strong className="text-slate-900">E2 Assist</strong> {c.assist.p1}
                </p>
                <p className="text-sm text-slate-500">{c.assist.note}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-7">
                <div className="flex items-center gap-2 mb-5">
                  <Wrench className="h-5 w-5 text-teal-600" />
                  <p className="font-semibold text-slate-900">{c.assist.panelTitle}</p>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {c.assist.functions.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ============ 13 — ONE PLATFORM, THREE EXPERIENCES ============ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="section-label">{c.platform.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
                {c.platform.h2}
              </h2>
            </div>
            <div className="max-w-2xl mx-auto flex flex-col items-center">
              {c.platform.items.map((p, i) => {
                const Icon = PLATFORM_ICONS[i];
                return (
                  <div key={p.name} className="w-full flex flex-col items-center">
                    <div className="w-full bg-slate-50 border border-slate-200 rounded-xl px-8 py-6 flex items-center gap-5 hover:border-teal-500 transition-colors" data-testid={`platform-${i}`}>
                      <div className="w-12 h-12 rounded-lg bg-teal-600 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">{p.name}</h3>
                        <p className="text-sm text-slate-600">{p.who}</p>
                      </div>
                    </div>
                    {i < 2 && <ArrowDown className="h-6 w-6 text-teal-400 my-3" aria-hidden="true" />}
                  </div>
                );
              })}
              <p className="mt-10 text-center text-lg text-slate-700 font-medium">
                {c.platform.statement}
              </p>
            </div>
          </div>
        </section>

        {/* ============ 14 — FROM EVENT TO ACTION ============ */}
        <section className="py-20 md:py-28 bg-slate-900 text-white">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">{c.workflow.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">{c.workflow.h2}</h2>
            </div>
            <div className="max-w-md mx-auto">
              {c.workflow.steps.map((step, i, arr) => (
                <div key={step} className="flex flex-col items-center">
                  <div className={`w-full text-center rounded-lg px-6 py-3.5 font-medium ${i === arr.length - 1 ? 'bg-teal-600 text-white' : 'bg-slate-800 border border-slate-700 text-slate-200'}`}>
                    {step}
                  </div>
                  {i < arr.length - 1 && <ArrowDown className="h-5 w-5 text-teal-500 my-2" aria-hidden="true" />}
                </div>
              ))}
            </div>
            <p className="mt-12 text-center text-slate-400 max-w-2xl mx-auto">{c.workflow.note}</p>
          </div>
        </section>

        {/* ============ 15 — FITS YOUR INSURANCE MODEL ============ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="section-label">{c.fits.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4">
                {c.fits.h2}
              </h2>
              <p className="text-lg text-slate-600">{c.fits.sub}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {c.fits.models.map((m, i) => {
                const Icon = FITS_ICONS[i];
                return (
                  <div key={m.title} className="bg-slate-50 border border-slate-200 rounded-xl p-8 hover:border-teal-500 transition-colors">
                    <div className="w-11 h-11 rounded-lg bg-teal-600/10 flex items-center justify-center mb-5">
                      <Icon className="h-5 w-5 text-teal-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{m.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                );
              })}
            </div>
            <p className="mt-12 text-center text-xl md:text-2xl font-semibold text-slate-900">
              {c.fits.highlight}
            </p>
          </div>
        </section>

        {/* ============ 16 — INSURANCE PRODUCT OPPORTUNITY ============ */}
        <section className="py-20 md:py-28 bg-slate-50">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="section-label">{c.opportunity.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
                {c.opportunity.h2}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {c.opportunity.models.map((m, i) => (
                <div key={m.title} className="bg-white border border-slate-200 rounded-xl p-8 hover:border-teal-500 transition-colors">
                  <p className="text-xs font-bold text-teal-600 uppercase tracking-widest mb-3">{c.opportunity.modelLabel} {i + 1}</p>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{m.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 17 — BUSINESS CASE CALCULATOR ============ */}
        <section className="py-20 md:py-28 bg-white" id="e2-calculator">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="section-label">{c.calculator.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4">
                {c.calculator.h2}
              </h2>
              <p className="text-lg text-slate-600">{c.calculator.sub}</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-8">
                <div className="flex items-center gap-2 mb-7">
                  <Calculator className="h-5 w-5 text-teal-600" />
                  <p className="font-semibold text-slate-900">{c.calculator.inputsTitle}</p>
                </div>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="calc-properties" className="block text-sm font-medium text-slate-700 mb-2">
                      {c.calculator.properties}
                    </label>
                    <input
                      id="calc-properties"
                      type="number"
                      min="0"
                      value={calc.properties}
                      onChange={(e) => handleCalcChange('properties', e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      data-testid="calc-properties-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="calc-frequency" className="block text-sm font-medium text-slate-700 mb-2">
                      {c.calculator.frequency}
                    </label>
                    <input
                      id="calc-frequency"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={calc.frequency}
                      onChange={(e) => handleCalcChange('frequency', e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      data-testid="calc-frequency-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="calc-avgcost" className="block text-sm font-medium text-slate-700 mb-2">
                      {c.calculator.avgCost}
                    </label>
                    <input
                      id="calc-avgcost"
                      type="number"
                      min="0"
                      value={calc.avgCost}
                      onChange={(e) => handleCalcChange('avgCost', e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      data-testid="calc-avgcost-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="calc-reduction" className="block text-sm font-medium text-slate-700 mb-2">
                      {c.calculator.reduction}
                    </label>
                    <input
                      id="calc-reduction"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={calc.reduction}
                      onChange={(e) => handleCalcChange('reduction', e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      data-testid="calc-reduction-input"
                    />
                    <p className="text-xs text-slate-500 mt-1.5">{c.calculator.reductionNote}</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-900 rounded-xl p-8 flex flex-col">
                <p className="text-xs font-bold uppercase tracking-widest text-teal-400 mb-7">
                  {c.calculator.resultsTag}
                </p>
                <div className="space-y-6 flex-1">
                  <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                    <p className="text-sm text-slate-400 mb-2">{c.calculator.claims}</p>
                    <p className="text-3xl font-bold text-white tracking-tight" data-testid="calc-claims-result">
                      {Math.round(annualClaims).toLocaleString('en-US')}
                    </p>
                  </div>
                  <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                    <p className="text-sm text-slate-400 mb-2">{c.calculator.exposure}</p>
                    <p className="text-3xl font-bold text-white tracking-tight" data-testid="calc-exposure-result">
                      {fmt(exposure)}
                    </p>
                  </div>
                  <div className="bg-teal-600/15 border border-teal-500/40 rounded-lg p-6">
                    <p className="text-sm text-teal-300 mb-2">{c.calculator.avoided}</p>
                    <p className="text-4xl font-bold text-teal-300 tracking-tight" data-testid="calc-avoided-result">
                      {fmt(avoided)}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] leading-relaxed text-slate-500 mt-7">{c.calculator.disclaimer}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ============ 18 — THE BUSINESS CASE ============ */}
        <section className="py-20 md:py-28 bg-slate-50">
          <div className="container-0g text-center">
            <p className="section-label">{c.businessCase.label}</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-10">
              {c.businessCase.h2}
            </h2>
            <p className="text-2xl md:text-4xl font-semibold text-slate-900 tracking-tight max-w-4xl mx-auto mb-10 leading-snug">
              {c.businessCase.statement}
            </p>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-10">{c.businessCase.support}</p>
            <p className="inline-block px-6 py-4 rounded-lg bg-slate-900 text-teal-300 text-lg font-semibold">
              {c.businessCase.highlight}
            </p>
          </div>
        </section>

        {/* ============ 19 — TRUST, SECURITY, TRACEABILITY ============ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="section-label">{c.trust.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
                {c.trust.h2}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {c.trust.items.map((item, i) => {
                const Icon = TRUST_ICONS[i];
                return (
                  <div key={item.title} className="bg-slate-50 border border-slate-200 rounded-xl p-7 hover:border-teal-500 transition-colors">
                    <Icon className="h-6 w-6 text-teal-600 mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============ 20 — BUILT AROUND THE INSURER ============ */}
        <section className="py-20 md:py-28 bg-slate-900 text-white">
          <div className="container-0g">
            <div className="max-w-3xl mb-12">
              <p className="text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">{c.adapt.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">{c.adapt.h2}</h2>
              <p className="text-lg text-slate-400">{c.adapt.sub}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {c.adapt.caps.map((cap) => (
                <span key={cap} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-800 border border-slate-700 text-sm font-medium text-slate-200 hover:border-teal-500/60 transition-colors">
                  <Settings className="h-4 w-4 text-teal-400" />
                  {cap}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 21 — WHITE LABEL ============ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-0g">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="section-label">{c.whiteLabel.label}</p>
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-6">
                  {c.whiteLabel.h2}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">{c.whiteLabel.p}</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-8">
                <div className="flex items-center gap-2 mb-5">
                  <Palette className="h-5 w-5 text-teal-600" />
                  <p className="font-semibold text-slate-900">{c.whiteLabel.panelTitle}</p>
                </div>
                <ul className="space-y-3">
                  {c.whiteLabel.elements.map((el) => (
                    <li key={el} className="flex items-center gap-3 text-slate-700">
                      <CheckCircle className="h-4 w-4 text-teal-600 flex-shrink-0" />
                      {el}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ============ 22 — FAQ ============ */}
        <section className="py-20 md:py-28 bg-slate-50" id="e2-faq">
          <div className="container-0g">
            <div className="max-w-3xl mb-14">
              <p className="section-label">{c.faq.label}</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
                {c.faq.h2}
              </h2>
            </div>
            <div className="max-w-3xl space-y-3">
              {c.faq.items.map((faq, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-slate-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                    aria-controls={`faq-answer-${i}`}
                    data-testid={`faq-question-${i}`}
                  >
                    <span className="font-semibold text-slate-900">{faq.q}</span>
                    {openFaq === i ? (
                      <ChevronUp className="h-5 w-5 text-teal-600 flex-shrink-0" aria-hidden="true" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" aria-hidden="true" />
                    )}
                  </button>
                  {openFaq === i && (
                    <div id={`faq-answer-${i}`} className="px-6 pb-6" data-testid={`faq-answer-${i}`}>
                      <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 23 — FINAL CTA ============ */}
        <section className="py-24 md:py-32 bg-slate-900 text-white">
          <div className="container-0g text-center">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-3">
              {c.finalCta.line1}
            </h2>
            <p className="text-3xl md:text-5xl font-semibold tracking-tight text-teal-400 mb-12">
              {c.finalCta.line2}
            </p>
            <div className="inline-flex items-center gap-3 mb-4">
              <Droplets className="h-7 w-7 text-teal-400" />
              <p className="text-2xl font-bold">E2 Insurance</p>
            </div>
            <p className="text-slate-400 mb-10">{c.finalCta.descriptor}</p>
            <Button
              size="lg"
              className="bg-teal-600 hover:bg-teal-500 text-white px-10"
              onClick={() => scrollTo('contact-form')}
              data-testid="final-cta-talk-btn"
            >
              {c.finalCta.cta}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* ============ 24 — CONTACT FORM ============ */}
        <div id="contact-form">
          <div className="bg-white pt-20 md:pt-24 pb-0">
            <div className="container-0g">
              <div className="max-w-3xl">
                <p className="section-label">{c.contact.label}</p>
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4">
                  {c.contact.h2}
                </h2>
                <p className="text-lg text-slate-600">{c.contact.sub}</p>
              </div>
            </div>
          </div>
          <ContactForm />
        </div>
      </main>

      <Footer />
    </>
  );
};

export default E2InsurancePage;
